import InteractiveAnnotation from "./interactiveAnnotation";
import { isInBoundsLine, displayAngle, normalizeAngle, segmentLengthDisp, isInBoundsBox } from "../geometry";
import { setLineStyle, setBoxStyle, drawBox, measureText, setFontStyle } from "../canvasUtils";
export default class Angle extends InteractiveAnnotation {
  constructor(position, options) {
    super(position, options);

    let { x, y } = position;

    this.cornerX = x;
    this.cornerY = y;

    this.edge1X = x - 100;
    this.edge1Y = y;

    this.edge2X = x;
    this.edge2Y = y - 100;

    this.maxArcRadius = 30;

    let d = Math.sqrt(10);
    this.textX = x + d;
    this.textY = y + d;
    this.font = options.textSize + "px Calibri";

    this.text = "90.0°";
    this.textWidth = 0;

    this.activeTip1 = false;
    this.activeTip2 = false;
    this.activeCorner = false;
    this.activeBodyOffset = false;
  }

  get edge1dy() { return this.edge1Y - this.cornerY; }
  get edge1dx() { return this.edge1X - this.cornerX; }

  get edge2dy() { return this.edge2Y - this.cornerY; }
  get edge2dx() { return this.edge2X - this.cornerX; }

  get edge1Angle() { return Math.atan2(this.edge1dy, this.edge1dx); }
  get edge2Angle() { return Math.atan2(this.edge2dy, this.edge2dx); }

  get edgeAngleDiff() { return normalizeAngle(this.edge1Angle - this.edge2Angle); }
  get angle() { return Math.abs(this.edgeAngleDiff); }
  get textAngle() {
    let angle = (this.edge1Angle + this.edge2Angle) / 2;
    if (this.flipText) angle += Math.PI;
    return normalizeAngle(angle);
  }
  get flipText() { return Math.abs(this.edge2Angle - this.edge1Angle) < Math.PI; }
  get flipArc() { return this.edgeAngleDiff > 0; }

  get edge1Length() { return segmentLengthDisp(this.edge1dx, this.edge1dy); }
  get edge2Length() { return segmentLengthDisp(this.edge2dx, this.edge2dy); }

  get arcRadius() {
    return Math.min(this.maxArcRadius, this.edge1Length / 2, this.edge2Length / 2);
  }

  get isActive() {
    return this.activeTip1 || this.activeTip2 || this.activeCorner || this.activeBodyOffset;
  }

  draw(ctx) {
    this.drawLines(ctx);
    this.drawArc(ctx);
    this.drawText(ctx);

    if (this.isActive) this.drawInteractionBoxes(ctx);
  }

  drawLines(ctx) {
    setLineStyle(ctx, this);

    ctx.beginPath();
    ctx.moveTo(this.edge1X, this.edge1Y);
    ctx.lineTo(this.cornerX, this.cornerY);
    ctx.stroke();
    ctx.closePath();

    ctx.beginPath();
    ctx.moveTo(this.cornerX, this.cornerY);
    ctx.lineTo(this.edge2X, this.edge2Y);
    ctx.stroke();
    ctx.closePath();
  }

  drawArc(ctx) {
    setBoxStyle(ctx);

    ctx.beginPath();
    ctx.arc(this.cornerX, this.cornerY, this.arcRadius, this.edge1Angle, this.edge2Angle, this.flipArc);
    ctx.stroke();
    ctx.closePath();
  }

  drawText(ctx) {
    setFontStyle(ctx, this);
    ctx.fillText(this.text, this.textX, this.textY);
  }

  drawInteractionBoxes(ctx) {
    drawBox(ctx, this.edge1X, this.edge1Y, this.transform.zoom);
    drawBox(ctx, this.edge2X, this.edge2Y, this.transform.zoom);
    drawBox(ctx, this.cornerX, this.cornerY, this.transform.zoom);
  }

  isInBounds(x, y) {
    return isInBoundsBox(x, y, this.cornerX, this.cornerY, this.transform.zoom)
      || isInBoundsBox(x, y, this.edge1X, this.edge1Y, this.transform.zoom)
      || isInBoundsBox(x, y, this.edge2X, this.edge2Y, this.transform.zoom)
      || this.isInBoundsText(x, y)
      || isInBoundsLine(x, y, this.edge1X, this.edge1Y, this.cornerX, this.cornerY)
      || isInBoundsLine(x, y, this.edge2X, this.edge2Y, this.cornerX, this.cornerY);
  }

  isInBoundsText(x, y) {
    return x >= this.textX && x <= this.textX + this.textWidth
      && y >= this.textY && y <= this.textY + 25;
  }

  mousedown(x, y) {
    super.mousedown(x, y);
    this.clearActiveState();

    if (isInBoundsBox(x, y, this.edge1X, this.edge1Y, this.transform.zoom)) {
      this.updateTip1(x, y);
      this.activeTip1 = true;
    } else if (isInBoundsBox(x, y, this.edge2X, this.edge2Y, this.transform.zoom)) {
      this.updateTip2(x, y);
      this.activeTip2 = true;
    } else if (isInBoundsBox(x, y, this.cornerX, this.cornerY, this.transform.zoom)) {
      this.updateCorner(x, y);
      this.activeCorner = true;
    } else this.activeBodyOffset = true;

    this.signalReady();
  }

  mousemove(x, y) {
    if (this.activeTip1) this.updateTip1(x, y);
    else if (this.activeTip2) this.updateTip2(x, y);
    else if (this.activeCorner) this.updateCorner(x, y);
    else if (this.activeBodyOffset) this.updateBodyOffset(x, y);

    this.calculateTextChanges();
    this.signalReady();
  }

  updateTip1(x, y) {
    this.edge1X = x;
    this.edge1Y = y;
  }

  updateTip2(x, y) {
    this.edge2X = x;
    this.edge2Y = y;
  }

  updateCorner(x, y) {
    this.cornerX = x;
    this.cornerY = y;
  }

  updateBodyOffset(x, y) {
    const { dx, dy } = this.getAndUpdateInteraction(x, y);
    this.updateTip1(this.edge1X + dx, this.edge1Y + dy);
    this.updateTip2(this.edge2X + dx, this.edge2Y + dy);
    this.updateCorner(this.cornerX + dx, this.cornerY + dy);
  }

  calculateTextChanges() {
    this.text = displayAngle(this.angle);
    this.textWidth = measureText(this.text);
    let x = this.cornerX + 15 * Math.cos(this.textAngle);
    let y = this.cornerY + 15 * Math.sin(this.textAngle);
    if (this.textAngle < (-Math.PI / 2) || this.textAngle > (Math.PI / 2)) x -= this.textWidth;
    if (this.textAngle < 0) y -= 25;
    this.textX = x;
    this.textY = y;
  }

  clearActiveState() {
    this.activeTip1 = false;
    this.activeTip2 = false;
    this.activeCorner = false;
    this.activeBodyOffset = false;
  }

  deactivate() {
    this.clearActiveState();
    this.signalReady();
  }

  updateTextSize(size) {
    this.setTextSize(size);
    this.font = this.options.textSize + "px Calibri";
    this.signalReady();
  }
}